import React from 'react';
import { observer } from 'mobx-react';
import dayjs from 'dayjs';
import { chunk } from 'lodash';

import { getErrorMessage, isNullOrEmpty } from 'common-utils';
import { IAccount } from 'dto/access-management/account-dto';
import { IDisplayDeploymentGroup } from 'dto/deployment/deployment-group-dto';
import { UnifiedDeploymentUnitType } from 'dto/deployment/deployment-dto';
import { ComponentDto, RaptorComponentViewModel } from 'dto/raptor-component.dto';
import { ComponentVersionDto, ComponentVersionStatus } from 'dto/raptor-component-version.dto';
import { useAccountStore, useDeploymentStore, useRaptorComponentsStore } from 'store';
import { HorizontalStepper, IStepItem, notification, Spinner } from 'ui-lib';

import {
    IRaptorComponentCompareState,
    IRaptorComponentCompareVersion,
    IRaptorDeploymentDraft,
} from '../deployment-create.types';
import { RaptorDeploymentHelpers } from './raptor-deployment.helpers';
import { SelectComponentsStep } from './wizard-steps/select-components-step';
import { SelectComponentVersionsStep } from './wizard-steps/select-component-versions-step';
import { CompareComponentsStep } from './wizard-steps/compare-components-step';
import { ScheduleStep } from './wizard-steps/schedule-step';
import { RaptorReviewStep } from './wizard-steps/review-step';
import { useLoadingStates } from 'hooks/use-loading.hook';
import deploymentCss from '../../deployments.css';
import { usePagination } from 'hooks/use-table-pagination.hook';
import { useDeploymentPageNavigation } from '../../hooks/use-deployment-page-navigation';

interface IRaptorDeploymentCreationWizardProps {
    account: IAccount;
    deploymentGroup: IDisplayDeploymentGroup;
    deploymentName: string;
    draft: IRaptorDeploymentDraft;
    onDraftChange: (draft: IRaptorDeploymentDraft) => void;
}

const SELECT_COMPONENTS_STEP_CODE = 'select-components';
const SELECT_COMPONENT_VERSIONS_STEP_CODE = 'select-component-versions';
const COMPARE_COMPONENTS_STEP_CODE = 'compare-components';
const SCHEDULE_STEP_CODE = 'schedule';
const REVIEW_STEP_CODE = 'review';

export const RaptorDeploymentCreationWizard = observer((props: IRaptorDeploymentCreationWizardProps): JSX.Element => {
    const { navigateToDeploymentList } = useDeploymentPageNavigation();
    const deploymentStore = useDeploymentStore();
    const componentsStore = useRaptorComponentsStore();
    const accountStore = useAccountStore();
    const mainLoadingHook = useLoadingStates();
    const [availableVersions, setAvailableVersions] = React.useState<Record<string, ComponentVersionDto[]>>({});
    const [componentComparison, setComponentComparison] = React.useState<IRaptorComponentCompareState>();

    const updateDraft = React.useCallback(
        (values: Partial<IRaptorDeploymentDraft>): void => props.onDraftChange({ ...props.draft, ...values }),
        [props.draft, props.onDraftChange],
    );

    const {
            data: components,
            loading,
            pagination,
            onTableChange,
            refresh: reloadComponentFn,
        } = usePagination({
            pageSize: 100,
            fetchData: async (params) => {
                const { pageSize, page } = params;
                const offset = (page - 1) * pageSize;
                const result = await componentsStore.getComponents(props.account.id, { accountUuid: props.account.uuid, offset, limit: pageSize, publishedOnly: true, includeSharedComponents: true });
    
                return {
                    data: result.map((component) => new RaptorComponentViewModel(component, accountStore)),
                };
            },
            enableShowTotal: true,
            cacheKey: `components-table`,
            mode: 'offset',
        });

    const loadPublishedComponentVersions = async (): Promise<boolean> => {
        const result = await mainLoadingHook.withLoading(async () => {
            setAvailableVersions({});
            const entries: Array<readonly [string, ComponentVersionDto[]]> = [];
            for (const componentBatch of chunk(props.draft.selectedComponents, 5)) {
                const batchEntries = await Promise.all(componentBatch.map(async (component) => {
                    const versions = await componentsStore.listComponentVersions(props.account.id, component.uuid, {
                        status: ComponentVersionStatus.PUBLISHED,
                    });
                    return [
                        component.uuid,
                        versions,
                    ] as const;
                }));
                entries.push(...batchEntries);
            }
            const nextAvailableVersions = Object.fromEntries(entries);
            setAvailableVersions(nextAvailableVersions);
            updateDraft({
                selectedComponentVersions: RaptorDeploymentHelpers.reconcileSelectedVersions(
                    props.draft.selectedComponents,
                    props.draft.selectedComponentVersions,
                    nextAvailableVersions,
                ),
            });
        });
        if (result.isFail()) {
            notification.error({
                message: 'Unable to load component versions',
                description: result.errorMessage,
            });
            return false;
        }
        return true;
    };

    const loadComponentComparison = async (): Promise<boolean> => {
        const result = await mainLoadingHook.withLoading(async () => {
            const latestDeployment = await deploymentStore.findLatestInProgressDeploymentInGroup(
                props.account.id,
                props.deploymentGroup.id,
                true,
            );
            const currentCandidates = latestDeployment
                ? RaptorDeploymentHelpers.extractComponentVersionCandidates(latestDeployment)
                : [];
            const currentVersions: IRaptorComponentCompareVersion[] = [];
            for (const versionUuidBatch of chunk(currentCandidates, 5)) {
                const batchVersions = await Promise.all(versionUuidBatch.map(async (versionUuid) => {
                    const componentVersion = await componentsStore.getComponentVersion(props.account.id, versionUuid);
                    return {
                        componentUuid: componentVersion.componentUuid,
                        componentName: componentVersion.componentName,
                        componentVersionUuid: componentVersion.uuid,
                        version: componentVersion.version,
                    };
                }));
                currentVersions.push(...batchVersions);
            }
            const targetVersions = props.draft.selectedComponents
                .map((component) => {
                    const componentVersion = props.draft.selectedComponentVersions[component.uuid];
                    if (!componentVersion) return null;
                    return RaptorDeploymentHelpers.toCompareVersion(component, componentVersion);
                })
                .filter((version): version is IRaptorComponentCompareVersion => !!version);

            setComponentComparison(RaptorDeploymentHelpers.buildCompareState(
                props.deploymentName,
                latestDeployment?.label || 'No active deployment',
                targetVersions,
                currentVersions,
            ));
        });
        if (result.isFail()) {
            notification.error({
                message: 'Unable to compare components',
                description: result.errorMessage,
            });
            return false;
        }
        return true;
    };

    const onComponentsChange = (selectedComponents: ComponentDto[]): void => {
        setAvailableVersions({});
        setComponentComparison(undefined);
        updateDraft({
            selectedComponents,
            selectedComponentVersions: RaptorDeploymentHelpers.removeVersionsForUnselectedComponents(
                props.draft.selectedComponentVersions,
                selectedComponents,
            ),
        });
    };

    const onFinish = async (): Promise<void> => {
        const scheduledDate = props.draft.selectedScheduleDate;
        if (!scheduledDate) {
            notification.error({
                message: 'Unable to create RAPTOR deployment',
                description: 'Select a scheduled date and time.',
            });
            return;
        }
        const result = await mainLoadingHook.withSaving(async () => {
            const created = await deploymentStore.createUnifiedDeployment(props.account.id, {
                groupId: props.deploymentGroup.id,
                label: props.deploymentName,
                accountUuid: props.deploymentGroup.accountUuid,
                deploymentContent: {
                    unitType: UnifiedDeploymentUnitType.COMPONENT_VERSION,
                    units: props.draft.selectedComponents.map((component) => ({
                        uuid: props.draft.selectedComponentVersions[component.uuid].uuid,
                        options: {},
                    })),
                },
                scheduleDateTime: scheduledDate.toISOString(),
            });
            notification.success({
                message: 'Deployment created',
                description: `Deployment "${props.deploymentName}" has been created successfully.`,
            });
            navigateToDeploymentList();
            return created;
        });
        if (result.isFail()) {
            return notification.error({
                message: 'Unable to create RAPTOR deployment',
                description: result.errorMessage,
            });
        }
    };

    const steps: IStepItem[] = [
        {
            code: SELECT_COMPONENTS_STEP_CODE,
            label: 'Select components',
            description: 'Choose one or more RAPTOR components',
            content: (
                <SelectComponentsStep
                    components={components}
                    selectedComponents={props.draft.selectedComponents}
                    loading={loading}
                    onChange={onComponentsChange}
                    pagination={pagination}
                    refresh={reloadComponentFn}
                    onTableChange={onTableChange}
                />
            ),
            navigation: {
                next: {
                    before: () => {
                        loadPublishedComponentVersions();
                    },
                    disabled: mainLoadingHook.loading || isNullOrEmpty(props.draft.selectedComponents),
                },
            },
        },
        {
            code: SELECT_COMPONENT_VERSIONS_STEP_CODE,
            label: 'Select component versions',
            description: 'Choose one published version for each component',
            content: (
                <SelectComponentVersionsStep
                    accountId={props.account.id}
                    selectedComponents={props.draft.selectedComponents}
                    availableVersions={availableVersions}
                    selectedVersions={props.draft.selectedComponentVersions}
                    loading={mainLoadingHook.loading}
                    onChange={(componentUuid, version): void => {
                        if (!version) return;
                        setComponentComparison(undefined);
                        updateDraft({
                            selectedComponentVersions: {
                                ...props.draft.selectedComponentVersions,
                                [componentUuid]: version,
                            },
                        });
                    }}
                />
            ),
            navigation: {
                next: {
                    disabled: mainLoadingHook.loading,
                    before: () => {
                        loadComponentComparison();
                    },
                },
            },
        },
        {
            code: COMPARE_COMPONENTS_STEP_CODE,
            label: 'Compare components',
            description: 'Compare components before deployment',
            content: (
                <CompareComponentsStep
                    accountId={props.account.id}
                    comparison={componentComparison}
                    loading={mainLoadingHook.loading}
                />
            ),
            navigation: {
                next: {
                    disabled: mainLoadingHook.loading,
                    before: (): void => {
                        if (!props.draft.selectedScheduleDate) {
                            const scheduledDate = dayjs().add(2, 'minutes').toDate();
                            scheduledDate.setSeconds(0);
                            updateDraft({ selectedScheduleDate: scheduledDate });
                        }
                    },
                },
            },
        },
        {
            code: SCHEDULE_STEP_CODE,
            label: 'Schedule',
            description: 'Choose when the deployment will start',
            content: (
                <ScheduleStep
                    selectedScheduleDate={props.draft.selectedScheduleDate}
                    deviceGroups={props.deploymentGroup.deviceGroupLabels ?? []}
                    onChange={(selectedScheduleDate): void => updateDraft({ selectedScheduleDate })}
                />
            ),
            navigation: {
                next: {
                    disabled: !props.draft.selectedScheduleDate,
                },
            },
        },
        {
            code: REVIEW_STEP_CODE,
            label: 'Review',
            description: 'Review and create the RAPTOR deployment',
            content: (
                <RaptorReviewStep
                    account={props.account}
                    deploymentGroup={props.deploymentGroup}
                    deploymentName={props.deploymentName}
                    selectedComponents={props.draft.selectedComponents}
                    selectedVersions={props.draft.selectedComponentVersions}
                    selectedScheduleDate={props.draft.selectedScheduleDate}
                />
            ),
        },
    ];

    return (
        <div className={deploymentCss['deployment-wizard']}>
            <Spinner spinning={mainLoadingHook.saving} style={{ display: 'block', width: '100%' }}>
                <HorizontalStepper
                    items={steps}
                    currentStepCode={SELECT_COMPONENTS_STEP_CODE}
                    actions={{
                        canGoBack: true,
                        finish: {
                            label: 'Create',
                            onClick: onFinish,
                        },
                    }}
                />
            </Spinner>
        </div>
    );
});
